import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Check,
  X,
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  Image,
  Archive,
  RotateCcw,
} from 'lucide-react';
import { cn } from '@lib/utils';
import { catalogService } from '@services/catalog.service';
import type { Brand } from '@apptypes/index';
import { PageGuard } from '@components/PageGuard';
import { PermissionGate } from '@components/PermissionGate';

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: Brand['status'] }) {
  const map = {
    approved: {
      label: 'Approved',
      icon: CheckCircle2,
      cls: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
    },
    pending_approval: {
      label: 'Pending',
      icon: Clock,
      cls: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
    },
    rejected: {
      label: 'Rejected',
      icon: XCircle,
      cls: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
    },
    archived: {
      label: 'Archived',
      icon: XCircle,
      cls: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
    },
  } as const;
  const cfg = map[status] ?? map.archived;
  const Icon = cfg.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold',
        cfg.cls
      )}
    >
      <Icon className="w-3 h-3" strokeWidth={1.5} />
      {cfg.label}
    </span>
  );
}

// ── Create modal ──────────────────────────────────────────────────────────────

function CreateBrandModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [logoUrl, setLogoUrl] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      catalogService.createBrand({
        name: name.trim(),
        description: description.trim() || undefined,
        logoUrl: logoUrl.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-[rgba(15,61,30,0.45)]">
      <div className="bg-white rounded-3xl shadow-lift w-full max-w-lg p-8">
        {/* Modal header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-[22px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            Create Brand
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Fields */}
        <div className="space-y-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Samsung"
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this brand"
              rows={3}
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors resize-none"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
              Logo URL
            </label>
            <input
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://..."
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
            />
          </div>

          {mutation.error && (
            <p className="text-[13px] text-[var(--state-danger)]">
              {(mutation.error as any)?.message ?? 'Something went wrong.'}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-[var(--border-subtle)]">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-all duration-200 hover:border-[var(--border-strong)]"
          >
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!name.trim() || mutation.isPending}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-50 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
          >
            {mutation.isPending ? 'Creating...' : 'Create Brand'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reject reason modal ───────────────────────────────────────────────────────

function RejectModal({
  title,
  onConfirm,
  onClose,
  isPending,
}: {
  title: string;
  onConfirm: (reason: string) => void;
  onClose: () => void;
  isPending: boolean;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-70 flex items-center justify-center p-4 bg-[rgba(15,61,30,0.45)]">
      <div className="bg-white rounded-3xl shadow-lift w-full max-w-lg p-8">
        {/* Modal header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-[22px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Field */}
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
            Reason *
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this brand is being rejected..."
            rows={3}
            className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors resize-none"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-[var(--border-subtle)]">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-all duration-200 hover:border-[var(--border-strong)]"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason.trim())}
            disabled={isPending || !reason.trim()}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-5 py-2.5 text-[14px] font-medium text-[var(--state-danger)] transition-all duration-200 hover:border-[var(--state-danger)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? 'Rejecting...' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export const BrandsPage = () => {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'archived'>('all');
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);

  const { data: brands = [], isLoading, error } = useQuery({
    queryKey: ['brands'],
    queryFn: () => catalogService.listBrands('all'),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => catalogService.approveBrand(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['brands'] }),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      catalogService.rejectBrand(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      setRejectTarget(null);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => catalogService.archiveBrand(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['brands'] }),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => catalogService.restoreBrand(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['brands'] }),
  });

  const renderBrandActions = (id: string, status: Brand['status']) => {
    const btn = 'flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-[12px] font-semibold transition-all duration-200 disabled:opacity-50';
    const approve = `${btn} bg-[var(--bg-elevated)] text-[var(--text-accent)] hover:bg-[var(--gradient-to)] hover:text-white`;
    const danger = `${btn} border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] text-[var(--state-danger)] hover:border-[var(--state-danger)]`;
    const neutral = `${btn} border border-[var(--border-default)] bg-white text-[var(--text-primary)] hover:border-[var(--border-strong)]`;

    if (status === 'pending_approval') {
      return (
        <PermissionGate permission="brands.approve">
          <div className="flex gap-2 pt-3 border-t border-[var(--border-subtle)] mt-auto">
            <button
              onClick={() => approveMutation.mutate(id)}
              disabled={approveMutation.isPending}
              className={approve}
            >
              <Check className="w-3.5 h-3.5" strokeWidth={1.5} />
              Approve
            </button>
            <button onClick={() => setRejectTarget(id)} className={danger}>
              <X className="w-3.5 h-3.5" strokeWidth={1.5} />
              Reject
            </button>
          </div>
        </PermissionGate>
      );
    }
    if (status === 'approved') {
      return (
        <PermissionGate permission="brands.approve">
          <div className="flex gap-2 pt-3 border-t border-[var(--border-subtle)] mt-auto">
            <button onClick={() => setRejectTarget(id)} className={danger}>
              <X className="w-3.5 h-3.5" strokeWidth={1.5} />
              Reject
            </button>
            <button
              onClick={() => archiveMutation.mutate(id)}
              disabled={archiveMutation.isPending}
              className={neutral}
            >
              <Archive className="w-3.5 h-3.5" strokeWidth={1.5} />
              Archive
            </button>
          </div>
        </PermissionGate>
      );
    }
    if (status === 'rejected') {
      return (
        <PermissionGate permission="brands.approve">
          <div className="flex gap-2 pt-3 border-t border-[var(--border-subtle)] mt-auto">
            <button
              onClick={() => restoreMutation.mutate(id)}
              disabled={restoreMutation.isPending}
              className={approve}
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
              Restore
            </button>
            <button
              onClick={() => archiveMutation.mutate(id)}
              disabled={archiveMutation.isPending}
              className={neutral}
            >
              <Archive className="w-3.5 h-3.5" strokeWidth={1.5} />
              Archive
            </button>
          </div>
        </PermissionGate>
      );
    }
    if (status === 'archived') {
      return (
        <PermissionGate permission="brands.approve">
          <div className="flex gap-2 pt-3 border-t border-[var(--border-subtle)] mt-auto">
            <button
              onClick={() => restoreMutation.mutate(id)}
              disabled={restoreMutation.isPending}
              className={approve}
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
              Restore
            </button>
          </div>
        </PermissionGate>
      );
    }
    return null;
  };

  const filtered = brands.filter((b) => {
    if (filter === 'pending' && b.status !== 'pending_approval') return false;
    if (filter === 'approved' && b.status !== 'approved') return false;
    if (filter === 'rejected' && b.status !== 'rejected') return false;
    if (filter === 'archived' && b.status !== 'archived') return false;
    if (search && !b.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const pendingCount = brands.filter((b) => b.status === 'pending_approval').length;

  return (
    <PageGuard permission="brands.read">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-1">
              Catalog
            </p>
            <h2 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
              Brands
            </h2>
            <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
              Product brands: approve submissions from merchants
            </p>
          </div>
          <PermissionGate permission="brands.create">
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              Add Brand
            </button>
          </PermissionGate>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search input */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]"
              strokeWidth={1.5}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search brands..."
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white pl-10 pr-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
            />
          </div>

          {/* Filter pill group */}
          <div className="flex rounded-2xl border border-[var(--border-subtle)] overflow-hidden bg-white">
            {(['all', 'pending', 'approved', 'rejected', 'archived'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  'px-4 py-2.5 text-[13px] font-medium transition-colors',
                  filter === f
                    ? 'bg-[var(--brand-forest)] text-white'
                    : 'text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)]'
                )}
              >
                {f === 'pending'
                  ? `Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}`
                  : f === 'all'
                  ? 'All'
                  : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="rounded-3xl bg-white shadow-soft p-12 text-center text-[14px] text-[var(--text-muted)]">
            Loading brands...
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="rounded-3xl bg-white shadow-soft p-12 text-center text-[14px] text-[var(--state-danger)]">
            Failed to load brands.
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && filtered.length === 0 && (
          <div className="rounded-3xl bg-white shadow-soft p-12 text-center text-[14px] text-[var(--text-muted)]">
            {search || filter !== 'all'
              ? 'No brands match your filters.'
              : 'No brands yet.'}
          </div>
        )}

        {/* Brand grid */}
        {!isLoading && !error && filtered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((brand) => (
              <div
                key={brand.id}
                className="rounded-3xl bg-white shadow-soft overflow-hidden flex flex-col"
              >
                {/* Logo area */}
                <div className="w-full h-28 bg-[var(--bg-base)] border-b border-[var(--border-subtle)] flex items-center justify-center overflow-hidden">
                  {brand.logoUrl ? (
                    <img
                      src={brand.logoUrl}
                      alt={brand.name}
                      className="w-full h-full object-contain p-3"
                    />
                  ) : (
                    <Image
                      className="w-10 h-10 text-[var(--text-muted)]"
                      strokeWidth={1.5}
                    />
                  )}
                </div>

                {/* Card body */}
                <div className="flex-1 flex flex-col gap-3 p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[15px] font-semibold text-[var(--text-primary)] truncate">
                        {brand.name}
                      </div>
                      {brand.description && (
                        <div className="text-[12px] text-[var(--text-muted)] mt-0.5 line-clamp-2">
                          {brand.description}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={brand.status} />
                  </div>

                  {brand.status === 'rejected' && brand.rejectionReason && (
                    <div className="text-[12px] text-[var(--state-danger)]">
                      Reason: {brand.rejectionReason}
                    </div>
                  )}

                  {renderBrandActions(brand.id, brand.status)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreate && <CreateBrandModal onClose={() => setShowCreate(false)} />}

      {rejectTarget && (
        <RejectModal
          title="Reject Brand"
          onConfirm={(reason) => rejectMutation.mutate({ id: rejectTarget, reason })}
          onClose={() => setRejectTarget(null)}
          isPending={rejectMutation.isPending}
        />
      )}
    </PageGuard>
  );
};
